/**
 * Pure ranking for Soren Home brief cards.
 * Priority: money → rights/deadlines → waiting. No I/O.
 */
import { formatMoney } from '../../lib/formatters'
import { appendSorenSource } from './sorenSession'
import { humanizeDaysRemaining } from './humanizeSorenDates'

export { formatShortDeadline, humanizeDaysRemaining } from './humanizeSorenDates'

export type SorenCardKind = 'money' | 'deadline' | 'document' | 'cadence' | 'retainage' | 'cash_delta' | 'friday_checkin'

export interface SorenCardAction {
  label: string
  href: string
}

/** One overdue Pay App row inside a grouped money card (max 5 shown). */
export interface SorenMoneyRow {
  payAppId: number
  projectId: number
  projectName: string
  amount: number
  daysOverdue: number
  openHref: string
  recordHref: string
}

export interface SorenCard {
  id: string
  kind: SorenCardKind
  title: string
  body: string
  primary: SorenCardAction
  secondary?: SorenCardAction
  /** Per–Pay App rows for money cards (group headline stays on title) */
  moneyRows?: SorenMoneyRow[]
  /** Overflow when >5 PAs: "and N more" → payments filter */
  moneyOverflow?: { count: number; href: string }
  /** WHAT / WHY / DO labels (P2 cadence + retainage) */
  what?: string
  why?: string
  doActions?: SorenCardAction[]
  /** Project for suppressions / Done / Remind persistence */
  projectId: number
  /** Underlying notification ids for mark-read / snooze */
  notificationIds?: number[]
  /** For metric strip aggregation */
  amount?: number
  daysRemaining?: number
  docCount?: number
  payAppCount?: number
  deadlineDate?: string | null
  /** Mechanics window ≤3d — promote to Do first */
  preferDoFirst?: boolean
  /** Friday check-in week (Monday ISO date) */
  weekOf?: string
}

export interface SorenPendingUpload {
  /** Stable key for collapse: sub + project */
  subKey: string
  subName: string
  projectId: number
  projectName: string
  uploadedAt: string
  docCount: number
  docLabels?: string[]
  notificationIds?: number[]
}

export interface SorenFollowUp {
  pay_app_id: number
  project_id: number
  project_name: string
  amount_due: number
  days_overdue: number
  app_number?: number | null
  owner_key: string
  owner_name: string
}

export interface SorenLien {
  id: number
  project_id: number
  project_name: string
  days_remaining: number
  deadline_date?: string | null
  filed_at?: string | null
  contract_amount?: number | null
}

export interface RankSorenInput {
  followUps?: SorenFollowUp[]
  liens?: SorenLien[]
  pendingUploads?: SorenPendingUpload[]
  /** P2: cadence + retainage WHAT/WHY/DO cards → Then review */
  reviewNudges?: SorenCard[]
  unreadNotificationCount?: number
  userName?: string | null
  now?: Date
}

export interface SorenQuietGroup {
  summary: string
  detailLines: string[]
}

export interface RankSorenResult {
  sentence: string
  doFirst: SorenCard[]
  thenReview: SorenCard[]
  quiet: string
  quietGroup: SorenQuietGroup
  count: number
  metrics: {
    moneyAtRisk: number
    nearestDeadlineLabel: string | null
    nearestDeadlineDays: number | null
    waitingDocs: number
  }
  glanceLine: string
}

const MAX_STACK = 8
const LIEN_URGENT_DAYS = 3

const MONEY_ROW_CAP = 5

function paymentsHref(projectId: number, highlightOutstanding: boolean, payAppId?: number): string {
  const base = `/projects?pm=${projectId}&tab=payments`
  const withHl = highlightOutstanding ? `${base}&hl=outstanding` : base
  const withPa = payAppId != null ? `${withHl}&pa=${payAppId}` : withHl
  return appendSorenSource(withPa)
}

function moneyRowFromFollowUp(item: SorenFollowUp): SorenMoneyRow {
  const overdue = Math.max(0, Math.trunc(Number(item.days_overdue) || 0))
  return {
    payAppId: item.pay_app_id,
    projectId: item.project_id,
    projectName: item.project_name,
    amount: Number(item.amount_due) || 0,
    daysOverdue: overdue,
    openHref: paymentsHref(item.project_id, true, item.pay_app_id),
    recordHref: paymentsHref(item.project_id, false, item.pay_app_id),
  }
}

/** @internal exported for unit tests */
export function buildMoneyCards(followUps: SorenFollowUp[]): SorenCard[] {
  const byOwner = new Map<string, SorenFollowUp[]>()
  for (const fu of followUps) {
    const key = fu.owner_key || fu.owner_name || `p-${fu.project_id}`
    if (!byOwner.has(key)) byOwner.set(key, [])
    byOwner.get(key)!.push(fu)
  }
  const cards: SorenCard[] = []
  for (const [, items] of byOwner) {
    items.sort((a, b) => b.days_overdue - a.days_overdue)
    const total = items.reduce((s, i) => s + (Number(i.amount_due) || 0), 0)
    const owner = items[0].owner_name || 'Owner'
    const primaryProject = items[0]
    const title =
      items.length === 1
        ? `${owner} owes ${formatMoney(total)} on ${items[0].project_name}`
        : `${formatMoney(total)} overdue across ${items.length} Pay Apps`
    const allRows = items.map(moneyRowFromFollowUp)
    const moneyRows = allRows.slice(0, MONEY_ROW_CAP)
    const overflowCount = Math.max(0, allRows.length - MONEY_ROW_CAP)
    cards.push({
      id: `money-${items.map((i) => i.pay_app_id).sort((a, b) => a - b).join('-')}`,
      kind: 'money',
      title,
      body: '',
      amount: total,
      payAppCount: items.length,
      projectId: primaryProject.project_id,
      notificationIds: [],
      moneyRows,
      moneyOverflow:
        overflowCount > 0
          ? {
              count: overflowCount,
              href: appendSorenSource('/projects?tab=payments&hl=outstanding'),
            }
          : undefined,
      // Fallback deep-link (card-level buttons hidden when moneyRows render)
      primary: {
        label: 'Open payments',
        href: paymentsHref(primaryProject.project_id, true),
      },
      secondary: {
        label: 'Record a payment',
        href: paymentsHref(primaryProject.project_id, false),
      },
    })
  }
  cards.sort((a, b) => (b.amount || 0) - (a.amount || 0))
  return cards
}

function buildLienCards(liens: SorenLien[]): {
  urgent: SorenCard[]
  laterQuiet: string | null
  laterCount: number
} {
  const urgentLiens: SorenLien[] = []
  const later: SorenLien[] = []
  for (const lien of liens) {
    if (lien.days_remaining <= LIEN_URGENT_DAYS) urgentLiens.push(lien)
    else later.push(lien)
  }

  const urgent: SorenCard[] = []
  if (urgentLiens.length === 1) {
    const lien = urgentLiens[0]
    const moneyBit =
      lien.contract_amount != null && Number.isFinite(lien.contract_amount)
        ? ` on ${formatMoney(lien.contract_amount)} of work`
        : ''
    const timing = humanizeDaysRemaining(lien.days_remaining, lien.deadline_date)
    urgent.push({
      id: `lien-${lien.id}`,
      kind: 'deadline',
      title: `Pre Lien for ${lien.project_name} — ${timing}`,
      body: `${lien.filed_at ? 'Filed.' : 'Not mailed yet.'}${moneyBit ? ` You may lose lien rights${moneyBit}.` : ''}`.trim(),
      daysRemaining: lien.days_remaining,
      deadlineDate: lien.deadline_date || null,
      projectId: lien.project_id,
      notificationIds: [],
      primary: {
        label: 'Mail Pre Lien',
        href: appendSorenSource(`/projects?pm=${lien.project_id}&tab=lien`),
      },
    })
  } else if (urgentLiens.length > 1) {
    const overdue = urgentLiens.filter((l) => l.days_remaining < 0)
    const dueSoon = urgentLiens.filter((l) => l.days_remaining >= 0)
    const n = urgentLiens.length
    const title = `${n} Pre Liens need review`
    const bodyParts: string[] = []
    if (overdue.length) {
      bodyParts.push(
        `${overdue.length} deadline${overdue.length === 1 ? '' : 's'} ${overdue.length === 1 ? 'has' : 'have'} passed`,
      )
    }
    if (dueSoon.length) {
      bodyParts.push(`${dueSoon.length} due soon`)
    }
    const nearest = [...urgentLiens].sort((a, b) => a.days_remaining - b.days_remaining)[0]
    const ids = urgentLiens
      .map((l) => l.id)
      .sort((a, b) => a - b)
      .join('-')
    urgent.push({
      id: `lien-group-${ids}`,
      kind: 'deadline',
      title,
      body: `${bodyParts.join(' · ')}. Soren grouped them instead of showing ${n} duplicate cards.`,
      daysRemaining: nearest.days_remaining,
      deadlineDate: nearest.deadline_date || null,
      projectId: nearest.project_id,
      notificationIds: [],
      primary: {
        label: 'Review Pre Liens',
        href: appendSorenSource(`/projects?pm=${nearest.project_id}&tab=lien`),
      },
    })
  }

  const laterQuiet =
    later.length > 0
      ? `${later.length} Pre Lien${later.length === 1 ? '' : 's'} due later`
      : null
  return { urgent, laterQuiet, laterCount: later.length }
}

function buildDocCards(pending: SorenPendingUpload[]): SorenCard[] {
  const map = new Map<string, SorenPendingUpload>()
  for (const row of pending) {
    const key = `${row.subKey}::${row.projectId}`
    const prev = map.get(key)
    if (!prev) {
      map.set(key, { ...row })
      continue
    }
    const older =
      new Date(row.uploadedAt).getTime() < new Date(prev.uploadedAt).getTime()
        ? row.uploadedAt
        : prev.uploadedAt
    map.set(key, {
      ...prev,
      docCount: prev.docCount + row.docCount,
      uploadedAt: older,
      docLabels: [...(prev.docLabels || []), ...(row.docLabels || [])],
      notificationIds: [
        ...(prev.notificationIds || []),
        ...(row.notificationIds || []),
      ],
    })
  }
  const merged = [...map.values()].sort(
    (a, b) => new Date(a.uploadedAt).getTime() - new Date(b.uploadedAt).getTime(),
  )

  // Group many waiting docs into one summary when 3+ distinct cards
  if (merged.length >= 3) {
    const totalDocs = merged.reduce((s, r) => s + r.docCount, 0)
    const first = merged[0]
    const allIds = merged.flatMap((r) => r.notificationIds || [])
    return [
      {
        id: `doc-group-${merged.map((r) => `${r.subKey}-${r.projectId}`).sort().join('_')}`,
        kind: 'document',
        title: `${totalDocs} documents are waiting`,
        body: `Grouped across ${merged.length} projects instead of showing separate cards.`,
        docCount: totalDocs,
        projectId: first.projectId,
        notificationIds: allIds,
        primary: {
          label: 'Review documents',
          href: appendSorenSource(`/projects?pm=${first.projectId}&tab=subs&filter=pending`),
        },
      },
    ]
  }

  return merged.map((row) => {
    const n = row.docCount
    const labels = (row.docLabels || []).filter(Boolean)
    const labelBit =
      labels.length > 0
        ? labels.slice(0, 3).join(', ')
        : n === 1
          ? 'a document'
          : `${n} documents`
    return {
      id: `doc-${row.subKey}-${row.projectId}`,
      kind: 'document' as const,
      title:
        n > 1 && labels.length === 0
          ? `${row.subName} sent ${n} documents on ${row.projectName}`
          : `${row.subName} sent ${labelBit} on ${row.projectName}`,
      body: 'Waiting for review in Project Hub.',
      docCount: n,
      projectId: row.projectId,
      notificationIds: row.notificationIds || [],
      primary: {
        label: n > 1 ? `Review ${n} documents` : 'Review',
        href: appendSorenSource(`/projects?pm=${row.projectId}&tab=subs&filter=pending`),
      },
    }
  })
}

function greeting(now: Date, userName?: string | null): string {
  const hour = now.getHours()
  const part = hour < 12 ? 'Morning' : hour < 17 ? 'Afternoon' : 'Evening'
  const name = (userName || '').trim().split(/\s+/)[0]
  return name ? `${part}, ${name}` : part
}

function composeSentence(
  doFirst: SorenCard[],
  thenReview: SorenCard[],
  userName: string | null | undefined,
  now: Date,
): string {
  const n = doFirst.length + thenReview.length
  if (n === 0) return `${greeting(now, userName)}. Nothing new today.`
  const things = n === 1 ? 'One thing needs you' : `${n} things need you`
  return `${greeting(now, userName)}. ${things}.`
}

function composeGlance(
  doFirst: SorenCard[],
  thenReview: SorenCard[],
  metrics: RankSorenResult['metrics'],
): string {
  const friday = [...doFirst, ...thenReview].find((c) => c.kind === 'friday_checkin')
  if (friday?.what) return friday.what

  const n = doFirst.length + thenReview.length
  if (n === 0) return 'Nothing new today. No uploads, no payments.'
  const bits: string[] = []
  if (metrics.moneyAtRisk > 0) bits.push(`${formatMoney(metrics.moneyAtRisk)} overdue`)
  if (
    metrics.nearestDeadlineLabel &&
    metrics.nearestDeadlineLabel !== 'none'
  ) {
    bits.push(metrics.nearestDeadlineLabel)
  }
  if (metrics.waitingDocs > 0) {
    bits.push(
      `${metrics.waitingDocs} document${metrics.waitingDocs === 1 ? '' : 's'} waiting`,
    )
  }
  if (bits.length === 0) return `${n} thing${n === 1 ? '' : 's'} need you.`
  return bits.join(' · ') + '. Everything else can wait.'
}

/** Next-deadline metric: upcoming only (≥ 0). Passed deadlines never appear here. */
export function formatNextDeadlineLabel(days: number | null | undefined): string {
  if (days == null || !Number.isFinite(days) || days < 0) return 'none'
  const d = Math.trunc(days)
  if (d === 0) return 'today'
  if (d === 1) return 'in 1 day'
  return `in ${d} days`
}

/**
 * rankSorenCards — pure.
 * Money first, then rights/deadlines, then waiting. Max 8; overflow → Quiet.
 */
export function rankSorenCards(input: RankSorenInput): RankSorenResult {
  const now = input.now || new Date()
  const followUps = input.followUps || []
  const liens = input.liens || []
  const pending = input.pendingUploads || []
  const unread = input.unreadNotificationCount || 0

  const { urgent: lienUrgent, laterQuiet, laterCount } = buildLienCards(liens)
  const moneyCards = buildMoneyCards(followUps)
  const docCards = buildDocCards(pending)
  const allReview = input.reviewNudges || []
  const deadlineDoFirst = allReview.filter(
    (c) => c.kind === 'deadline' && (c.preferDoFirst || (c.daysRemaining != null && c.daysRemaining <= LIEN_URGENT_DAYS)),
  )
  const deadlineThen = allReview.filter(
    (c) => c.kind === 'deadline' && !deadlineDoFirst.includes(c),
  )
  const fridayCards = allReview.filter((c) => c.kind === 'friday_checkin')
  const reviewNudges = allReview.filter(
    (c) => c.kind === 'cadence' || c.kind === 'retainage' || c.kind === 'cash_delta',
  )

  // Do first: Friday check-in (top slot Fri–Sun), then money, rights/deadlines
  const doFirstAll = [...fridayCards, ...moneyCards, ...lienUrgent, ...deadlineDoFirst]
  // Then review: cadence/retainage + 4–29d mechanics, then docs
  const thenReviewAll = [...deadlineThen, ...reviewNudges, ...docCards]

  // Reserve up to 2 stack slots for review nudges so money cards cannot crowd them out
  const reviewReserve = Math.min(2, reviewNudges.length + deadlineThen.length)
  const doCap = Math.max(fridayCards.length, MAX_STACK - reviewReserve)
  const doFirstTrimmed = doFirstAll.slice(0, doCap)
  const thenCap = MAX_STACK - doFirstTrimmed.length
  const thenTrimmed = thenReviewAll.slice(0, thenCap)
  const overflow = [
    ...doFirstAll.slice(doCap),
    ...thenReviewAll.slice(thenCap),
  ]

  const moneyKept = doFirstTrimmed.filter((c) => c.kind === 'money')
  const deadlineKept = doFirstTrimmed.filter((c) => c.kind === 'deadline')
  const doFirstFinal = [...moneyKept, ...deadlineKept]
  const thenReview = thenTrimmed.filter(
    (c) => c.kind === 'document' || c.kind === 'cadence' || c.kind === 'retainage'
      || c.kind === 'cash_delta' || c.kind === 'friday_checkin' || c.kind === 'deadline',
  )

  const passedDeadlines = liens.filter((l) => l.days_remaining < 0).length

  const detailLines: string[] = []
  if (passedDeadlines > 0) {
    detailLines.push(
      `${passedDeadlines} deadline${passedDeadlines === 1 ? '' : 's'} passed`,
    )
  }
  if (laterQuiet) detailLines.push(laterQuiet)
  if (overflow.length > 0) {
    detailLines.push(
      `${overflow.length} more item${overflow.length === 1 ? '' : 's'} queued`,
    )
  }
  if (unread > 0) {
    detailLines.push(`${unread} notification${unread === 1 ? '' : 's'} unread`)
  }

  const quietCount =
    passedDeadlines + laterCount + overflow.length + (unread > 0 ? unread : 0)
  const quietSummary =
    quietCount > 0
      ? `${quietCount} lower-priority item${quietCount === 1 ? '' : 's'} are being watched. They will not interrupt you unless urgency changes.`
      : doFirstFinal.length + thenReview.length === 0
        ? 'No uploads, no payments.'
        : 'Everything else can wait.'

  const quiet =
    detailLines.length > 0
      ? detailLines.join(' · ')
      : quietSummary

  const quietGroup: SorenQuietGroup = {
    summary: quietSummary,
    detailLines:
      detailLines.length > 0
        ? detailLines
        : doFirstFinal.length + thenReview.length === 0
          ? []
          : ['Nothing urgent in Quiet right now.'],
  }

  const allMoney = moneyCards.reduce((s, c) => s + (c.amount || 0), 0)

  const upcomingDays = liens
    .map((l) => l.days_remaining)
    .filter((d): d is number => d != null && Number.isFinite(d) && d >= 0)
  const nearestDeadlineDays =
    upcomingDays.length > 0 ? Math.min(...upcomingDays) : null
  const nearestDeadlineLabel = formatNextDeadlineLabel(nearestDeadlineDays)

  const waitingDocs = pending.reduce((s, p) => s + p.docCount, 0)

  const metrics = {
    moneyAtRisk: allMoney,
    nearestDeadlineLabel,
    nearestDeadlineDays,
    waitingDocs,
  }

  const sentence = composeSentence(doFirstFinal, thenReview, input.userName, now)
  const glanceLine = composeGlance(doFirstFinal, thenReview, metrics)
  const count = doFirstFinal.length + thenReview.length

  return {
    sentence,
    doFirst: doFirstFinal,
    thenReview,
    quiet,
    quietGroup,
    count,
    metrics,
    glanceLine,
  }
}

/** Assert sheet money strings always have two decimals (no bare $ abbreviate). */
export function assertMoneyHasCents(text: string): boolean {
  const matches = text.match(/\$[\d,]+(?:\.\d+)?/g) || []
  return matches.every((m) => /\$[\d,]+\.\d{2}$/.test(m))
}
